import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button, Input, message } from "antd";
import moment from "moment";
import CryptoJS from "crypto-js";
import DatePicker from "react-bootstrap-daterangepicker"; // Import DatePicker
import "bootstrap/dist/css/bootstrap.min.css"; // Import Bootstrap CSS
import "bootstrap-daterangepicker/daterangepicker.css"; // Import DatePicker CSS
import { renderDate } from "./utils/dateFormatter";

const { Search } = Input;
const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
const secretKey = "abde100199199111";

const GuestCardView = () => {
  const business_date = localStorage.getItem("business_date"); // e.g., "2025-02-23"

  // Initialize dateRange with business_date or today as a single-day range
  const [dateRange, setDateRange] = useState(() => {
    const defaultDate = business_date && moment(business_date).isValid()
      ? moment(business_date).startOf("day")
      : moment().startOf("day");
    return {
      startDate: defaultDate,
      endDate: defaultDate,
    };
  });

  const [searchText, setSearchText] = useState("");
  const [guestData, setGuestData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchGuests(dateRange.startDate, dateRange.endDate);
  }, []); // Empty dependency array to run only on mount

  const fetchGuests = async (startDate, endDate) => {
    // Validate dates before proceeding
    if (!startDate || !endDate || !moment(startDate).isValid() || !moment(endDate).isValid()) {
      message.error("Invalid date range provided.");
      setGuestData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(
        `${apiURL}/booking/reservations?arrivals=true&startDate=${startDate.format("YYYY-MM-DD")}&endDate=${endDate.format("YYYY-MM-DD")}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      if (data.success) {
        setGuestData(
          data.reservations.map((guest) => ({
            ...guest,
            num_adults: guest.num_adults || 0,
            num_children: guest.num_children || 0,
          }))
        );
      } else {
        message.error("Failed to fetch reservations.");
        setGuestData([]);
      }
    } catch (error) {
      message.error("Error fetching reservations.");
      setGuestData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (param) => {
    setDateRange((prevRange) => {
      let newRange;
      if (typeof param === "string") {
        if (param === "prev") {
          newRange = {
            startDate: prevRange.startDate.clone().subtract(1, "day").startOf("day"),
            endDate: prevRange.endDate.clone().subtract(1, "day").startOf("day"),
          };
        } else if (param === "next") {
          newRange = {
            startDate: prevRange.startDate.clone().add(1, "day").startOf("day"),
            endDate: prevRange.endDate.clone().add(1, "day").startOf("day"),
          };
        } else {
          return prevRange;
        }
      } else {
        const { start, end } = param;
        newRange = {
          startDate: moment(start).startOf("day"),
          endDate: moment(end).startOf("day"),
        };
      }

      // Validate dates before calling API
      if (!newRange.startDate.isValid() || !newRange.endDate.isValid()) {
        message.error("Invalid date selected.");
        setGuestData([]);
        return prevRange;
      }

      if (newRange.startDate.isAfter(newRange.endDate)) {
        message.error("Start date cannot be after end date.");
        setGuestData([]);
        return prevRange;
      }

      // Fetch guests with the new date range
      fetchGuests(newRange.startDate, newRange.endDate);

      return newRange;
    });
  };

  const encryptId = (id) => {
    return CryptoJS.AES.encrypt(id.toString(), secretKey).toString();
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <h2 className="mb-0">Arrivals</h2>
            <div className="d-flex align-items-center gap-2">
              <DatePicker
                initialSettings={{
                  singleDatePicker: false, // Allow range selection
                  showDropdowns: true,
                  startDate: dateRange.startDate.toDate(),
                  endDate: dateRange.endDate.toDate(),
                  ranges: {
                    "Today": [dateRange.startDate.toDate(), dateRange.startDate.toDate()],
                    "This Week": [
                      moment(dateRange.startDate).startOf("week").toDate(),
                      moment(dateRange.startDate).endOf("week").toDate(),
                    ],
                    "This Month": [
                      moment(dateRange.startDate).startOf("month").toDate(),
                      moment(dateRange.startDate).endOf("month").toDate(),
                    ],
                    "Next Month": [
                      moment(dateRange.startDate).add(1, "month").startOf("month").toDate(),
                      moment(dateRange.startDate).add(1, "month").endOf("month").toDate(),
                    ],
                  },
                  locale: {
                    format: "DD MMM YYYY",
                    applyLabel: "Select",
                    cancelLabel: "Cancel",
                  },
                }}
                onCallback={(start, end) => {
                  handleDateChange({ start, end });
                }}
              >
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                    style={{
                      width: "220px",
                      height: "38px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      textAlign: "center",
                      padding: "0 10px 0 10px",
                      borderRight: "none",
                      backgroundColor : "#ffffff",
                    }}
                    readOnly
                  />
                  <div className="input-group-append">
                    <span
                      className="input-group-text"
                      style={{
                        height: "38px",
                        backgroundColor: "#fff",
                        borderLeft: "none",
                        borderRadius: "0 4px 4px 0",
                        cursor: "pointer",
                      }}
                    >
                      <i className="fa fa-chevron-down" style={{ color: "#495057" }} />
                    </span>
                  </div>
                </div>
              </DatePicker>
            </div>
            <Search
              placeholder="Search by Name, Email, or Booking ID"
              allowClear
              enterButton="Search"
              size="large"
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: 450 }}
            />
            <div className="ms-auto">
              <Link to="/hospitality/guest-reservation" className="btn add-btn custom-btn">
                <i className="la la-plus-circle custom-icon" /> Add New Reservation
              </Link>
            </div>
          </div>
        </div>

        {loading ? (
          <div id="loader-wrapper-">
            <div className="loader">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        ) : (
          <div className="row">
            {guestData && guestData.length > 0 ? (
              guestData
                .filter((guest) =>
                  guest?.guest_name?.toLowerCase().includes(searchText.toLowerCase())
                )
                .map((guest) => (
                  <div className="col-md-4 col-sm-6 col-12 col-lg-3 col-xl-3" key={guest.booking_id}>
                    <div className="profile-bg shadow-sm rounded p-3 position-relative" style={{ marginBottom: "15px" }}>
                      {/* Guest Name */}
                      <div className="d-flex align-items-center mt-1">
                        <i className="fas fa-user-circle me-2" style={{ color: "#2d2e83", fontSize: "18px" }}></i>
                        <h5 className="user-name">
                          <Link to={`/hospitality/reservation-details/${encodeURIComponent(encryptId(guest.booking_id))}`}>
                            {guest?.guest_name || "N/A"}
                          </Link>
                        </h5>
                      </div>

                    {/* Expected Time */}
{/* Check-in / Check-out with Status */}
<div className="profile-details mt-2">
  <p className="d-flex align-items-center mb-1">
    <i className="fas fa-calendar-alt me-2" style={{ color: "#2d2e83", fontSize: "16px" }}></i>
    <span className="rounded bg-success"  style={{ padding: "3px 10px" }}>
      {guest?.check_in_date && guest?.check_out_date
        ? `${renderDate(guest.check_in_date)} - ${renderDate(guest.check_out_date)}`
        : "N/A"}
    </span>
    <span
      className={`ms-3 px-2 py-1 rounded text-white status-badge-bottom ${
        guest?.booking_status === "checked_in" ? "bg-success" : "bg-warning"
      }`}
      style={{ fontSize: "12px" }}
    >
      {guest?.booking_status === "checked_in" ? "CHECKED-IN" : "DUE"}
    </span>
  </p>

  {/* Optional Expected Time */}
  {guest?.expected_time && (
    <small className="text-warning d-flex align-items-center ms-4">
      <i className="fas fa-clock me-2" style={{ fontSize: "13px" }}></i>
      Expected at {guest.expected_time}
    </small>
  )}
</div>



                      {/* Contact Details */}
                      <ul className="profile-details">
                        <li>
                          <i className="fas fa-mobile me-2" style={{ color: "#2d2e83", fontSize: "16px" }}></i>
                          {guest?.guest_phone || "N/A"}
                        </li>
                      </ul>

                      {/* Room & Guests Info */}
                      <ul className="profile-details">
                        <li>
                          <i className="fas fa-bed me-2" style={{ color: "#2d2e83", fontSize: "16px" }}></i>
                          {guest?.room_types || "N/A"}
                        </li>
                        <li>
                          <i className="fas fa-users me-2" style={{ color: "#2d2e83", fontSize: "16px" }}></i>
                          {guest?.num_adults || 0} <i className="fas fa-male ms-1 text-primary"></i>,
                          {guest?.num_children || 0} <i className="fas fa-child ms-1 text-warning"></i>
                        </li>
                      </ul>

                      {/* Status Badge */}
                      <div className={`status-badge-bottom ${guest?.booking_status === "checked_in" ? "bg-success" : "bg-warning"}`}>
                        {guest?.booking_status === "checked_in" ? "CHECKED-IN" : "DUE"}
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              <div className="no-data-container text-center mt-4">
                <img
                  src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                  alt="No data available"
                  style={{ width: "150px", marginBottom: "20px" }}
                />
                <h3>No Data Available</h3>
                <p>No guests are expected to arrive in this date range.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestCardView;